import { getAssistance } from "./admin-assistance";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError, performApiRequest } from "./api";
import {
  enqueueProviderMutation,
  createOfflineRecordId,
  acquireSyncLease,
  releaseSyncLease,
  renewSyncLease,
  isSyncLeaseCurrent,
  withOfflineReplayLock,
  getOfflineSessionRevision,
  isOfflineSessionCurrent,
  deadLetterProviderMutation,
  getOfflineIdentityScope,
  getQueuedProviderMutationCount,
  getOfflineResourceMapping,
  listQueuedProviderMutations,
  listDeadLetterProviderMutations,
  retryDeadLetterProviderMutation,
  listOfflineResourceMappings,
  removeQueuedProviderMutation,
  subscribeToOfflineStore,
  writeOfflineResourceMapping,
  type QueuedProviderMutation,
} from "./offline-store";

interface QueueMutationInput {
  body: unknown;
  client_mutation_id?: string;
  label: string;
  local_resource_id?: string;
  method: QueuedProviderMutation["method"];
  path: string;
  response_id_field?: string;
}

interface ProviderOfflineContextValue {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncError: string | null;
  pendingCount: number;
  deadLetterCount: number;
  deadLetters: QueuedProviderMutation[];
  retryDeadLetter: (mutationId: string) => void;
  dismissDeadLetter: (mutationId: string) => void;
  queueMutation: (input: QueueMutationInput) => void;
  syncPendingMutations: () => Promise<void>;
  syncVersion: number;
}

const ProviderOfflineContext = createContext<ProviderOfflineContextValue | null>(null);

function getOnlineState() {
  if (typeof navigator === "undefined") {
    return true;
  }

  return navigator.onLine;
}

function getMutationErrorMessage(error: unknown) {
  if (error instanceof ApiError) {
    return error.detail;
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return "Unable to sync queued provider changes.";
}

function isStaleSessionRevisionError(error: unknown) {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    error.detail === "stale_session_revision"
  );
}

const PROVIDER_SESSION_EXTENSION_PATH = /^\/api\/v1\/provider\/sessions\/([^/]+)\/extend$/;
const STALE_SESSION_REVISION_DEPENDENCY_REASON = "Blocked by stale session revision dependency.";

function getProviderSessionExtensionId(path: string) {
  return PROVIDER_SESSION_EXTENSION_PATH.exec(path)?.[1] ?? null;
}

function replaceLocalIdsInValue(
  value: unknown,
  resourceMap: Map<string, string>,
): unknown {
  if (typeof value === "string") {
    return resourceMap.get(value) ?? value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => replaceLocalIdsInValue(item, resourceMap));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        replaceLocalIdsInValue(nestedValue, resourceMap),
      ]),
    );
  }

  return value;
}

function replaceLocalIdsInPath(path: string, resourceMap: Map<string, string>) {
  let resolvedPath = path;

  for (const [localId, serverId] of resourceMap.entries()) {
    resolvedPath = resolvedPath.replaceAll(localId, serverId);
  }

  return resolvedPath;
}

function isOfflineRecordId(value: string) {
  return /^offline-[a-z]+-[a-z0-9]+-[a-z0-9]+$/i.test(value);
}

function hasUnresolvedOfflineId(value: unknown): boolean {
  if (typeof value === "string") {
    return isOfflineRecordId(value);
  }

  if (Array.isArray(value)) {
    return value.some((item) => hasUnresolvedOfflineId(item));
  }

  if (value && typeof value === "object") {
    return Object.values(value).some((item) => hasUnresolvedOfflineId(item));
  }

  return false;
}

function extractResponseId(response: unknown, responseIdField: string) {
  if (
    response &&
    typeof response === "object" &&
    responseIdField in response &&
    typeof response[responseIdField as keyof typeof response] === "string"
  ) {
    return response[responseIdField as keyof typeof response] as string;
  }

  return null;
}

function StandardProviderOfflineProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(getOnlineState);
  const [pendingCount, setPendingCount] = useState(getQueuedProviderMutationCount);
  const [deadLetterCount, setDeadLetterCount] = useState(() => listDeadLetterProviderMutations().length);
  const [deadLetters, setDeadLetters] = useState(listDeadLetterProviderMutations);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncError, setLastSyncError] = useState<string | null>(null);
  const [syncVersion, setSyncVersion] = useState(0);
  const isSyncingRef = useRef(false);
  const leaseOwnerRef = useRef(createOfflineRecordId("tab"));

  const refreshQueueState = useCallback(() => {
    setPendingCount(getQueuedProviderMutationCount());
    setDeadLetterCount(listDeadLetterProviderMutations().length);
    setDeadLetters(listDeadLetterProviderMutations());
  }, []);

  const retryDeadLetter = useCallback((mutationId: string) => {
    retryDeadLetterProviderMutation(mutationId);
    refreshQueueState();
  }, [refreshQueueState]);

  const dismissDeadLetter = useCallback((mutationId: string) => {
    removeQueuedProviderMutation(mutationId);
    refreshQueueState();
  }, [refreshQueueState]);

  const syncPendingMutations = useCallback(async (background = false) => {
    if (getAssistance()) return;
    await withOfflineReplayLock(async () => {
    if (!getOnlineState() || isSyncingRef.current) {
      return;
    }

    const queuedMutations = listQueuedProviderMutations();
    if (queuedMutations.length === 0) {
      setLastSyncError(null);
      setPendingCount(0);
      return;
    }

    const lease = acquireSyncLease(leaseOwnerRef.current);
    if (!lease) return;
    isSyncingRef.current = true;
    setIsSyncing(true);
    let processedCount = 0;
    let requiresAuthoritativeRefresh = false;
    const resourceMap = new Map(Object.entries(listOfflineResourceMappings()));
    const sessionRevision = getOfflineSessionRevision().revision;

    try {
      for (const mutation of queuedMutations) {
        const currentLease = renewSyncLease(lease);
        if (
          !currentLease ||
          !isSyncLeaseCurrent(currentLease) ||
          mutation.identity_scope !== getOfflineIdentityScope() ||
          sessionRevision !== getOfflineSessionRevision().revision ||
          !isOfflineSessionCurrent()
        ) {
          break;
        }
        let resolvedPath = mutation.path;
        try {
          resolvedPath = replaceLocalIdsInPath(mutation.path, resourceMap);
          const resolvedBody = replaceLocalIdsInValue(mutation.body, resourceMap);

          const unresolvedPathSegments = resolvedPath
            .split("/")
            .filter((segment) => isOfflineRecordId(segment));

          if (unresolvedPathSegments.length > 0 || hasUnresolvedOfflineId(resolvedBody)) {
            throw new Error(
              `Queued change "${mutation.label}" depends on another offline record that has not synced yet.`,
            );
          }

          const response = await performApiRequest<unknown>(resolvedPath, {
            method: mutation.method,
            body: resolvedBody,
            disableOfflineFallback: true,
            headers: mutation.client_mutation_id
              ? { "x-client-mutation-id": mutation.client_mutation_id }
              : undefined,
          });

          const committed = (() => {
            if (
              !isSyncLeaseCurrent(currentLease) ||
              sessionRevision !== getOfflineSessionRevision().revision ||
              !isOfflineSessionCurrent() ||
              mutation.identity_scope !== getOfflineIdentityScope()
            ) return false;
            if (mutation.local_resource_id) {
              const responseId = extractResponseId(response, mutation.response_id_field ?? "id");
              if (responseId) {
                resourceMap.set(mutation.local_resource_id, responseId);
                writeOfflineResourceMapping(mutation.local_resource_id, responseId);
              }
            }
            removeQueuedProviderMutation(mutation.id);
            return true;
          })();
          if (committed) processedCount += 1;
        } catch (error) {
          setLastSyncError(getMutationErrorMessage(error));
          if (isStaleSessionRevisionError(error)) {
            requiresAuthoritativeRefresh = true;
            const failedSessionId = getProviderSessionExtensionId(resolvedPath);
            const mutationIndex = queuedMutations.findIndex(({ id }) => id === mutation.id);
            if (failedSessionId !== null && mutationIndex >= 0) {
              for (const dependent of queuedMutations.slice(mutationIndex + 1)) {
                if (dependent.identity_scope !== mutation.identity_scope) continue;
                const dependentSessionId = getProviderSessionExtensionId(
                  replaceLocalIdsInPath(dependent.path, resourceMap),
                );
                if (dependentSessionId === failedSessionId) {
                  deadLetterProviderMutation(
                    dependent.id,
                    STALE_SESSION_REVISION_DEPENDENCY_REASON,
                  );
                }
              }
            }
          }
          if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429) {
            deadLetterProviderMutation(mutation.id, getMutationErrorMessage(error));
          }
          break;
        }
      }

      if (processedCount > 0 || requiresAuthoritativeRefresh) {
        setSyncVersion((current) => current + 1);
      }

      if (getQueuedProviderMutationCount() === 0) {
        setLastSyncError(null);
      }
    } finally {
      refreshQueueState();
      isSyncingRef.current = false;
      setIsSyncing(false);
      releaseSyncLease(lease);
    }
    }, background);
  }, [refreshQueueState]);

  const queueMutation = useCallback(
    (input: QueueMutationInput) => {
      if (getAssistance()) throw new ApiError(0, "Admin assistance requires a connection. Reconnect and try again.");
      const queued = enqueueProviderMutation(input);
      refreshQueueState();

      // Ask the service worker to replay mutations when the browser comes back
      // online, even if this tab is closed.
      if ("serviceWorker" in navigator && "SyncManager" in window) {
        void navigator.serviceWorker.ready.then((registration) =>
          (registration as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }).sync
            ?.register("gaamed-provider-sync")
            .catch(() => undefined),
        );
      }

      if (getOnlineState()) {
        void syncPendingMutations(true);
      }

      return queued;
    },
    [refreshQueueState, syncPendingMutations],
  );

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      void syncPendingMutations(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "SYNC_PENDING") {
        void syncPendingMutations(true);
      }
    };

    const unsubscribeStore = subscribeToOfflineStore(refreshQueueState);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);

    return () => {
      unsubscribeStore();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
    };
  }, [refreshQueueState, syncPendingMutations]);

  const value = useMemo(
    () => ({
      isOnline,
      isSyncing,
      lastSyncError,
      pendingCount,
      deadLetterCount,
      deadLetters,
      retryDeadLetter,
      dismissDeadLetter,
      queueMutation,
      syncPendingMutations,
      syncVersion,
    }),
    [
      isOnline,
      isSyncing,
      lastSyncError,
      pendingCount,
      deadLetterCount,
      deadLetters,
      retryDeadLetter,
      dismissDeadLetter,
      queueMutation,
      syncPendingMutations,
      syncVersion,
    ],
  );

  return (
    <ProviderOfflineContext.Provider value={value}>
      {children}
    </ProviderOfflineContext.Provider>
  );
}

export function ProviderOfflineProvider({ children }: {
  children: ReactNode;
}) {
  return <StandardProviderOfflineProvider>{children}</StandardProviderOfflineProvider>;
}

export function useProviderOffline() {
  const value = useContext(ProviderOfflineContext);

  if (!value) {
    throw new Error("useProviderOffline must be used within ProviderOfflineProvider");
  }

  return value;
}
